import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { Alert } from "react-bootstrap";

const EditLocationForm = ({ location, onCancel, onUpdated }) => {
  const [states, setStates] = useState([]);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const { tenant_id, user } = useContext(AuthContext);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await secureAxios.get("/api/states");
        setStates(res.data);
      } catch (err) {
        console.error("Failed to load states:", err);
        setMessage("Failed to load states. Please refresh the page.");
        setMessageType("warning");
      }
    };

    fetchStates();
  }, []);

  useEffect(() => {
    if (location) {
      setFormData({
        location_id: location.location_id,
        location_name: location.location_name || "",
        entity_type: location.entity_type || "",
        address_line_1: location.address?.address_line_1 || "",
        address_line_2: location.address?.address_line_2 || "",
        city: location.address?.city || "",
        state_id: location.address?.state_id || "",
        postal_code: location.address?.postal_code || "",
      });
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedState = states.find((s) => s.state_id === formData.state_id);
    const stateCode = selectedState?.state_code || "";
    const fullAddress = `${formData.address_line_1}, ${formData.city}, ${stateCode} ${formData.postal_code}`;

    let latitude = null;
    let longitude = null;

    try {
      // Attempt geocoding
      const geoRes = await secureAxios.get("/api/geocode", {
        params: { q: fullAddress },
      });

      if (geoRes.data && geoRes.data.length > 0) {
        latitude = geoRes.data[0].lat;
        longitude = geoRes.data[0].lon;
      }
    } catch (geoErr) {
      console.warn("⚠️ Geocoding failed, continuing without lat/lng", geoErr);
    }

    const payload = {
      ...formData,
      tenant_id,
      updated_by: user?.user_id || "system",
      latitude,
      longitude,
    };

    try {
      await secureAxios.put(`/api/locations/${formData.location_id}`, payload);

      setMessage("✅ Location updated successfully!");
      setMessageType("success");

      // Auto-close after successful update
      setTimeout(() => {
        if (onUpdated) onUpdated();
      }, 1500);
    } catch (err) {
      console.error("❌ Error updating location:", err);
      setMessage("❌ Failed to update location. Please try again.");
      setMessageType("danger");
    }
  };

  // Auto-hide message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="container mt-3">
      <h4>Edit Location</h4>

      {message && (
        <Alert variant={messageType} onClose={() => setMessage("")} dismissible>
          {message}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-md-6">
          <label htmlFor="location_name" className="form-label">
            Location Name
          </label>
          <input
            id="location_name"
            className="form-control"
            name="location_name"
            value={formData.location_name || ""}
            onChange={handleChange}
            required
            autoComplete="organization"
          />
        </div>

        <div className="col-md-6">
          <label htmlFor="entity_type" className="form-label">
            Entity Type
          </label>
          <input
            id="entity_type"
            className="form-control"
            name="entity_type"
            value={formData.entity_type || ""}
            onChange={handleChange}
            autoComplete="off"
          />
        </div>

        <div className="col-md-6">
          <label htmlFor="address_line_1" className="form-label">
            Address Line 1
          </label>
          <input
            id="address_line_1"
            className="form-control"
            name="address_line_1"
            value={formData.address_line_1 || ""}
            onChange={handleChange}
            required
            autoComplete="address-line1"
          />
        </div>

        <div className="col-md-6">
          <label htmlFor="address_line_2" className="form-label">
            Address Line 2
          </label>
          <input
            id="address_line_2"
            className="form-control"
            name="address_line_2"
            value={formData.address_line_2 || ""}
            onChange={handleChange}
            autoComplete="address-line2"
          />
        </div>

        <div className="col-md-4">
          <label htmlFor="city" className="form-label">
            City
          </label>
          <input
            id="city"
            className="form-control"
            name="city"
            value={formData.city || ""}
            onChange={handleChange}
            required
            autoComplete="address-level2"
          />
        </div>

        <div className="col-md-4">
          <label htmlFor="state_id" className="form-label">
            State
          </label>
          <select
            id="state_id"
            className="form-select"
            name="state_id"
            value={formData.state_id || ""}
            onChange={handleChange}
            required
            autoComplete="address-level1"
          >
            <option value="">-- Select State --</option>
            {states.map((s) => (
              <option key={s.state_id} value={s.state_id}>
                {s.state_code} - {s.state_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label htmlFor="postal_code" className="form-label">
            Postal Code
          </label>
          <input
            id="postal_code"
            className="form-control"
            name="postal_code"
            value={formData.postal_code || ""}
            onChange={handleChange}
            required
            autoComplete="postal-code"
          />
        </div>

        <div className="col-12 d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Update Location
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditLocationForm;
